import { useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function ImageLightbox({
  images,
  activeIndex,
  onClose,
  onPrev,
  onNext,
  title,
  testIdPrefix = "image-lightbox",
}) {
  const imageCount = images.length;
  const currentImage = images[activeIndex];

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (imageCount > 1 && event.key === "ArrowLeft") onPrev();
      if (imageCount > 1 && event.key === "ArrowRight") onNext();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [imageCount, onClose, onNext, onPrev]);

  if (!currentImage) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-[#05070C]/94 backdrop-blur-md px-4 py-6"
      onClick={onClose}
      data-testid={`${testIdPrefix}-overlay`}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/[0.08] bg-black/35 text-brand-text-secondary transition-colors duration-200 hover:text-brand-text hover:border-brand-gold/40"
        data-testid={`${testIdPrefix}-close`}
      >
        <X size={18} />
      </button>

      {imageCount > 1 && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 md:left-6 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/[0.08] bg-black/35 text-brand-text-secondary transition-colors duration-200 hover:text-brand-text hover:border-brand-gold/40"
          data-testid={`${testIdPrefix}-prev`}
        >
          <ChevronLeft size={18} />
        </button>
      )}

      <div
        className="relative w-full max-w-7xl"
        onClick={(event) => event.stopPropagation()}
        data-testid={`${testIdPrefix}-content`}
      >
        <div className="overflow-hidden rounded-sm border border-white/[0.08] bg-[#090B11] shadow-[0_25px_80px_rgba(0,0,0,0.45)]">
          <div className="flex items-center justify-between gap-4 border-b border-white/[0.06] px-5 py-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-brand-gold">Fullscreen Artwork View</p>
              <p className="mt-1 text-[13px] text-brand-text-secondary">
                {title}
                {imageCount > 1 ? ` · ${activeIndex + 1} / ${imageCount}` : ""}
              </p>
            </div>
          </div>

          <div className="flex min-h-[60vh] max-h-[84vh] items-center justify-center px-4 py-4 md:px-8 md:py-8">
            <img
              src={currentImage.url}
              alt={currentImage.altText || title}
              className="max-h-[76vh] w-auto max-w-full object-contain"
              data-testid={`${testIdPrefix}-image`}
            />
          </div>
        </div>
      </div>

      {imageCount > 1 && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onNext();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 md:right-6 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/[0.08] bg-black/35 text-brand-text-secondary transition-colors duration-200 hover:text-brand-text hover:border-brand-gold/40"
          data-testid={`${testIdPrefix}-next`}
        >
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  );
}
